import React from 'react';
import { X, Angry, Trash2, Edit3, Star } from 'lucide-react';

interface PhotoItemProps {
  blobFileName: string;
  index: number;
  title: string;
  isLoading: boolean;
  hasError: boolean;
  disabledAddOrRemove?: boolean;
  disabledRemove?: boolean;
  fileName?: string;
  notes?: string;
  onPhotoClick: (index: number) => void;
  onDeleteClick: (index: number) => void;
  onNoteEdit?: (index: number) => void;
  hideTag?: boolean;
  isDeleting?: boolean;
  isCover?: boolean;
  onSetCover?: (index: number) => void;
  captionMode?: boolean;
}

const PhotoItem: React.FC<PhotoItemProps> = ({
  blobFileName,
  index,
  title,
  isLoading,
  hasError,
  disabledAddOrRemove = false,
  disabledRemove = false,
  fileName,
  notes,
  onPhotoClick,
  onDeleteClick,
  onNoteEdit,
  hideTag = false,
  isDeleting = false,
  isCover = false,
  onSetCover,
  captionMode = false,
}) => {
  return (
    <div className="relative aspect-square min-h-31">
      {isLoading ? (
        <div className="w-full h-full flex items-center justify-center bg-gray-200 rounded-lg">
          <svg className="animate-spin h-5 w-5 text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
          </svg>
        </div>
      ) : hasError ? (
        <div className="w-full h-full flex flex-col items-center justify-center bg-red-200 rounded-lg text-red-600 gap-2">
          <Angry size={40} />
          <span className="text-sm text-center break-words w-full">ไม่สามารถโหลดรูปได้</span>
          <span className="text-xs text-center break-all w-full">{fileName}</span>
        </div>
      ) : isDeleting ? (
        <div className="w-full h-full flex flex-col items-center justify-center bg-gray-200 rounded-lg text-gray-600 gap-2">
          <div className="relative">
            <Trash2 size={40} className="animate-pulse" />
            <div className="absolute inset-0 flex items-center justify-center">
              <svg className="animate-spin h-8 w-8 text-red-500 opacity-70" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
              </svg>
            </div>
          </div>
          <span className="text-sm text-center font-medium">กำลังลบรูปภาพ...</span>
          <span className="text-xs text-center break-all w-full opacity-75">{fileName}</span>
        </div>
      ) : (
        <div className="relative w-full h-full">
          <img
            src={blobFileName}
            alt={fileName}
            className="w-full h-full object-cover rounded-lg cursor-pointer"
            onClick={() => onPhotoClick(index)}
          />
          {/* Notes indicator and preview */}
          {notes && (
            <div className="absolute bottom-2 left-2 bg-black bg-opacity-75 text-white px-2 py-1 rounded-md text-xs max-w-[80%]">
              <div className="truncate">{notes}</div>
            </div>
          )}
          {/* Note edit button */}
          {!disabledAddOrRemove && !isDeleting && onNoteEdit && (
            <button
              onClick={() => onNoteEdit(index)}
              className={captionMode
                ? `absolute bottom-2 right-2 flex items-center gap-1 bg-white px-2 py-1 text-xs font-medium text-blue-600 shadow-md hover:bg-gray-100 ${notes ? 'rounded-full' : 'rounded-md'}`
                : 'absolute bottom-2 right-2 p-1 bg-white rounded-full shadow-md hover:bg-gray-100'}
              title={captionMode ? 'แก้ไขคำบรรยาย' : 'เพิ่มหมายเหตุ'}
              aria-label={captionMode
                ? notes ? 'แก้ไขคำบรรยายรูปภาพ' : 'เพิ่มคำบรรยายรูปภาพ'
                : 'เพิ่มหมายเหตุรูปภาพ'}
            >
              <Edit3 className="w-4 h-4 text-blue-500" />
              {captionMode && !notes && <span>เพิ่มคำบรรยาย</span>}
            </button>
          )}
        </div>
      )}

      {!hideTag && <div className="absolute top-2 left-2 bg-white px-3 py-1 rounded-md">
        <span className="text-blue-600 font-medium">{title}</span>
      </div>}

      {onSetCover && !isDeleting && (
        <button onClick={() => onSetCover(index)} className={`absolute top-2 left-2 rounded-full p-1 shadow-md ${isCover ? 'bg-amber-100 text-amber-600' : 'bg-white text-gray-400 hover:text-amber-500'}`} title="ตั้งเป็นภาพหน้าปก" aria-label="ตั้งเป็นภาพหน้าปก">
          <Star className="w-4 h-4" fill={isCover ? 'currentColor' : 'none'} />
        </button>
      )}

      {!disabledAddOrRemove && !isDeleting && !disabledRemove && (
        <button
          onClick={() => onDeleteClick(index)}
          data-cy={`delete-photo-${fileName}`}
          className="absolute top-2 right-2 p-1 bg-white rounded-full shadow-md hover:bg-gray-100"
          disabled={isDeleting}
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

export default PhotoItem;
